package se.mittpass.web.components

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import se.mittpass.shared.api.SlotDto

@Composable
internal fun YearView(
    year: Int,
    slots: List<SlotDto>,
    isMobile: Boolean,
    isNarrow: Boolean,
    onDayClick: (day: String) -> Unit,
    onWeekClick: (weekMonday: String) -> Unit,
    absences: List<se.mittpass.shared.api.AbsenceRequestDto> = emptyList()
) {
    val countByDay = remember(slots) { slots.groupingBy { it.day }.eachCount() }
    val today = todayLocalDate()

    val cols = when {
        isMobile -> 1
        isNarrow -> 2
        else -> 4
    }

    Div({
        style {
            property("display", "grid")
            property("grid-template-columns", "repeat($cols, 1fr)")
            property("gap", if (isMobile) "1rem" else "1.5rem")
        }
    }) {
        for (m in 1..12) {
            MiniMonth(year, m, countByDay, today, onDayClick, onWeekClick, absences)
        }
    }
}

@Composable
private fun MiniMonth(
    year: Int,
    month: Int,
    countByDay: Map<String, Int>,
    today: String,
    onDayClick: (String) -> Unit,
    onWeekClick: (String) -> Unit,
    absences: List<se.mittpass.shared.api.AbsenceRequestDto> = emptyList()
) {
    val first = "$year-${month.toString().padStart(2, '0')}-01"
    val gridStart = mondayOf(first)
    val isSummer = month in 6..8

    Div({
        style {
            property("display", "flex"); property("flex-direction", "column"); property("gap", "0.5rem")
            if (isSummer) {
                property("background", "var(--rp-info-bg)"); property("padding", "0.5rem"); property("border-radius", "12px")
                property("border", "1px solid var(--rp-info-bg)")
            }
        }
    }) {
        Div({ style {
            property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between")
        } }) {
            Span({ style {
                property("font-weight", "700"); property("font-size", "0.95rem"); property("color", if (isSummer) "var(--rp-iris)" else "var(--rp-text)")
                property("letter-spacing", "-0.2px")
            } }) { Text(MONTH_NAMES[month - 1].replaceFirstChar { it.uppercase() }) }
            if (isSummer) {
                Span({ style {
                    property("background", "var(--rp-info-bg)"); property("color", "var(--rp-iris)")
                    property("padding", "0.15rem 0.45rem"); property("border-radius", "50px")
                    property("font-size", "0.65rem"); property("font-weight", "700")
                } }) { Text("Sommarsemester") }
            }
        }

        Div({ style {
            property("display", "grid")
            property("grid-template-columns", "22px repeat(7, 1fr)")
            property("gap", "2px")
            property("align-items", "center")
        } }) {
            // Header: "V." column header + M T O T F L S
            Div({ style {
                property("text-align", "center"); property("font-size", "0.58rem")
                property("font-weight", "800"); property("color", "var(--rp-iris)")
                property("padding", "0.15rem 0")
            } }) { Text("V.") }

            for (i in 0..6) {
                Div({ style {
                    property("text-align", "center"); property("font-size", "0.6rem")
                    property("font-weight", "700"); property("color", "var(--rp-muted)")
                    property("padding", "0.15rem 0")
                } }) { Text(DAY_NAMES_SHORT[i].take(1)) }
            }

            for (row in 0 until 6) {
                val rowStartIdx = row * 7
                val rowMonday = addDays(gridStart, rowStartIdx)
                val wkNum = weekNumber(rowMonday)

                // 1. Week Number Badge Cell
                Div({
                    onClick { onWeekClick(rowMonday) }
                    attr("title", "Öppna vecka $wkNum")
                    style {
                        property("aspect-ratio", "1 / 1")
                        property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                        property("font-size", "0.56rem"); property("font-weight", "800")
                        property("border-radius", "4px")
                        property("background", "var(--rp-info-bg)"); property("color", "var(--rp-iris)")
                        property("cursor", "pointer")
                        property("user-select", "none")
                    }
                }) {
                    Text(wkNum.toString())
                }

                // 2. 7 Days of the Week Row
                for (col in 0 until 7) {
                    val idx = rowStartIdx + col
                    val cellDate = addDays(gridStart, idx)
                    val parts = cellDate.split("-")
                    val cellMonth = parts[1].toInt()
                    val dayNum = parts[2].toInt()
                    val isOtherMonth = cellMonth != month
                    val isToday = cellDate == today
                    val shiftCount = countByDay[cellDate] ?: 0
                    val dayAbsences = absences.filter { abs -> cellDate >= abs.startDate && cellDate <= abs.endDate }
                    val hasVacation = dayAbsences.isNotEmpty()

                    val intensity = when {
                        shiftCount == 0 -> 0
                        shiftCount <= 2 -> 1
                        shiftCount <= 5 -> 2
                        else -> 3
                    }
                    val bg = when {
                        isOtherMonth -> "transparent"
                        hasVacation && shiftCount > 0 -> "linear-gradient(135deg, var(--rp-muted) 50%, var(--rp-warn-bg) 50%)"
                        hasVacation -> "var(--rp-warn-bg)"
                        intensity == 0 -> "var(--rp-overlay)"
                        intensity == 1 -> "var(--rp-info-border)"
                        intensity == 2 -> "var(--rp-muted)"
                        else -> "var(--rp-pine)"
                    }
                    val color = when {
                        isOtherMonth -> "var(--rp-hl-med)"
                        hasVacation -> "var(--rp-warn-text)"
                        intensity >= 2 -> "var(--rp-surface)"
                        else -> "var(--rp-subtle)"
                    }
                    val tooltipText = buildString {
                        append(cellDate)
                        if (shiftCount > 0) append(" • $shiftCount pass")
                        if (hasVacation) append(" • ${dayAbsences.size} semester/frånvaro")
                    }

                    Div({
                        onClick { if (!isOtherMonth) onDayClick(cellDate) }
                        attr("title", tooltipText)
                        style {
                            property("aspect-ratio", "1 / 1")
                            property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                            property("font-size", "0.62rem"); property("font-weight", "600")
                            property("border-radius", "5px")
                            property("position", "relative")
                            property("background", bg); property("color", color)
                            if (hasVacation && !isOtherMonth) property("border", "1px solid var(--rp-warn-border)")
                            if (!isOtherMonth) property("cursor", "pointer")
                            if (isToday) {
                                property("outline", "2px solid var(--rp-pine)")
                                property("outline-offset", "-1px")
                            }
                        }
                    }) {
                        Text(if (isOtherMonth) "" else dayNum.toString())
                    }
                }
            }
        }
    }
}
